/**
 * Runs — the trail of every agent run in this organization's workspaces: who
 * ran which agent and model, when, what came of it, what it cost, and which
 * hosts its sandbox was refused. Never what was asked or written: the record
 * holds counts, names and domains (`@airlock/contracts/runs/record`).
 *
 * A member reads their own runs; an owner or an administrator reads every
 * member's and may narrow to one. The Worker decides which (`readOrgRuns`),
 * never this page. Every filter is one value and empty is no filter, so
 * clearing them is the page as it opened. A row opens to say the rest — the
 * hosts, the pull requests, the tokens — rather than carrying them in columns
 * nobody can read at once.
 */
import { useCallback, useEffect, useState } from 'react';
import type React from 'react';
import { formatTokens, formatUsd } from '@airlock/contracts/usage/analysis';
import { RUN_OUTCOMES, RUN_OUTCOME_WORDS, totalTokens, type OrgRunsAnswer, type RunOutcome, type RunRow } from '@airlock/contracts/runs/record';
import { color, font } from '../../theme/tokens';
import { Callout, Empty, Input, Row, StatusCell, Table, type BadgeTone } from '../../components/primitives';
import { HeaderButton } from '../../components/Shell';
import { api, API_BASE, apiPath, apiQuery } from '../../lib/api';
import type { Organization, Seat, WorkspaceList, WorkspaceSummary } from '../../lib/types';
import { failureText, stamp } from './shared';
import { DateCell } from '../../components/DateCell';
import { notify } from '../../components/Toast';
import { PageError, SkeletonRows, useLoadPhase } from '../../components/Loading';
import { FilterBar, FilterCount, QuietButton, filterDate, filterSelect } from '../../components/FilterBar';

const TONE: Record<RunOutcome, BadgeTone> = { running: 'off', landed: 'ok', 'pr-opened': 'ok', abandoned: 'warn', failed: 'bad' };
const AGENTS = [
  { value: 'claude-code', label: 'Claude Code' },
  { value: 'codex', label: 'Codex' }
];

export function RunsPage({ organization, admin }: { organization: Organization | null; admin: boolean }): React.JSX.Element {
  const [member, setMember] = useState('');
  const [workspace, setWorkspace] = useState('');
  const [agent, setAgent] = useState('');
  const [outcome, setOutcome] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [answer, setAnswer] = useState<OrgRunsAnswer | null>(null);
  const [runs, setRuns] = useState<RunRow[] | null>(null);
  const phase = useLoadPhase(runs === null);
  const [open, setOpen] = useState<string | null>(null);
  const [workspaces, setWorkspaces] = useState<WorkspaceSummary[]>([]);
  const [seats, setSeats] = useState<Seat[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [older, setOlder] = useState(false);
  const columns = '130px minmax(0,1.2fr) minmax(0,1fr) minmax(0,1fr) 110px minmax(0,1fr) 80px';

  const query = useCallback(
    (cursor: string | null): string => {
      const params = new URLSearchParams();
      if (member) params.set('member', member);
      if (workspace) params.set('workspace', workspace);
      if (agent) params.set('agent', agent);
      if (outcome) params.set('outcome', outcome);
      if (from) params.set('from', from);
      if (to) params.set('to', to);
      if (cursor) params.set('cursor', cursor);
      return params.toString();
    },
    [member, workspace, agent, outcome, from, to]
  );

  const load = useCallback(
    async (cursor: string | null): Promise<void> => {
      if (!organization) return;
      try {
        const next = await api.get<OrgRunsAnswer>(apiPath`/orgs/${organization.id}/runs?${apiQuery(new URLSearchParams(query(cursor)))}`);
        setError(null);
        setAnswer(next);
        setRuns((current) => (cursor && current ? [...current, ...next.runs] : next.runs));
      } catch (failure) {
        if (!cursor) setRuns([]);
        setError(failureText(failure, 'The runs could not be read.'));
      }
    },
    [organization?.id, query]
  );

  useEffect(() => {
    setRuns(null);
    void load(null);
  }, [load]);

  useEffect(() => {
    if (!organization) return;
    api
      .get<WorkspaceList>(apiPath`/orgs/${organization.id}/workspaces`)
      .then((list) => setWorkspaces(list.workspaces))
      .catch(() => setWorkspaces([]));
    if (admin) {
      api
        .get<{ seats: Seat[] }>(apiPath`/orgs/${organization.id}/seats`)
        .then((list) => setSeats(list.seats))
        .catch(() => setSeats([]));
    }
  }, [organization?.id, admin]);

  if (!organization) {
    return <Empty title="Runs belong to an organization">A personal account’s runs stay on its machine.</Empty>;
  }

  const narrowed = Boolean(member || workspace || agent || outcome || from || to);
  const everyone = answer?.everyone ?? admin;
  const clear = (): void => {
    setMember('');
    setWorkspace('');
    setAgent('');
    setOutcome('');
    setFrom('');
    setTo('');
  };

  return (
    <>
      {answer && !answer.collects && (
        <Callout tone="amber" title="This organization does not record runs">
          {admin
            ? 'Turn on Trail of runs in Configuration › Agents › All agents and publish: from then on each member’s Airlock sends a record of every run in this organization’s workspaces, and is told it does.'
            : 'An owner or an administrator turns it on. Until then nothing is sent; the runs below, if any, were sent while it was on.'}
        </Callout>
      )}
      <Table
        columns={columns}
        header={['Started', everyone ? 'Member' : 'Workspace', everyone ? 'Workspace' : 'Task', 'Agent', 'Outcome', 'Work', 'Cost']}
        frame="clamp(640px, 75vh, 1440px)"
        toolbar={
          <FilterBar
            end={
              <>
                {runs !== null && <FilterCount shown={runs.length} total={answer?.total ?? null} narrowed={narrowed} noun={['run', 'runs']} />}
                <QuietButton
                  href={`${API_BASE}/orgs/${encodeURIComponent(organization.id)}/runs.csv?${apiQuery(new URLSearchParams(query(null)))}`}
                  onClick={() => notify(`Export started — runs-${new Date().toISOString().slice(0, 10)}.csv`)}
                >
                  Export CSV
                </QuietButton>
              </>
            }
          >
            {everyone && (
              <select aria-label="Member" value={member} onChange={(event) => setMember(event.target.value)} style={filterSelect(Boolean(member))}>
                <option value="">Every member</option>
                {seats.map((seat) => (
                  <option key={seat.id} value={seat.email.toLowerCase()}>
                    {seat.email}
                  </option>
                ))}
              </select>
            )}
            <select aria-label="Workspace" value={workspace} onChange={(event) => setWorkspace(event.target.value)} style={filterSelect(Boolean(workspace))}>
              <option value="">Every workspace</option>
              {workspaces.map((one) => (
                <option key={one.id} value={one.id}>
                  {one.name}
                </option>
              ))}
            </select>
            <select aria-label="Agent" value={agent} onChange={(event) => setAgent(event.target.value)} style={filterSelect(Boolean(agent))}>
              <option value="">Every agent</option>
              {AGENTS.map((one) => (
                <option key={one.value} value={one.value}>
                  {one.label}
                </option>
              ))}
            </select>
            <select aria-label="Outcome" value={outcome} onChange={(event) => setOutcome(event.target.value)} style={filterSelect(Boolean(outcome))}>
              <option value="">Every outcome</option>
              {RUN_OUTCOMES.map((one) => (
                <option key={one} value={one}>
                  {RUN_OUTCOME_WORDS[one]}
                </option>
              ))}
            </select>
            <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <Input type="date" aria-label="From" value={from} max={to || undefined} onChange={(event) => setFrom(event.target.value)} style={filterDate(Boolean(from))} />
              <span style={{ fontSize: 12.5, color: color.inkFaint }}>to</span>
              <Input type="date" aria-label="To" value={to} min={from || undefined} onChange={(event) => setTo(event.target.value)} style={filterDate(Boolean(to))} />
            </span>
            {narrowed && <QuietButton onClick={clear}>Clear</QuietButton>}
          </FilterBar>
        }
      >
        {runs === null || phase !== 'content' ? (
          phase === 'skeleton' && <SkeletonRows columns={columns} />
        ) : (
          runs.map((run) => {
            const key = `${run.member.email}/${run.id}`;
            return (
              <div key={key}>
                <button
                  type="button"
                  aria-expanded={open === key}
                  onClick={() => setOpen(open === key ? null : key)}
                  style={{ all: 'unset', display: 'block', width: '100%', cursor: 'pointer' }}
                >
                  <Row columns={columns}>
                    <DateCell at={run.startedAt} style={{ fontFamily: font.mono, fontSize: 12, color: color.inkSubtle }} />
                    <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {everyone ? run.member.name || run.member.email : (run.workspaceName ?? 'A workspace')}
                    </span>
                    <span style={{ minWidth: 0, color: color.inkBody, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {everyone ? (run.workspaceName ?? 'A workspace') : (run.taskId ?? '—')}
                    </span>
                    <span style={{ fontFamily: font.mono, fontSize: 12, color: color.inkBody }}>
                      {run.agent} · {run.model}
                    </span>
                    <StatusCell tone={TONE[run.outcome]} word={RUN_OUTCOME_WORDS[run.outcome]} />
                    <span style={{ fontFamily: font.mono, fontSize: 12, color: color.inkSubtle }}>
                      {run.turns} turn{run.turns === 1 ? '' : 's'} · +{run.linesAdded} −{run.linesRemoved}
                      {run.refusedHosts.length ? <span style={{ color: color.warnText }}> · {run.refusedHosts.length} refused</span> : null}
                    </span>
                    <span style={{ fontFamily: font.mono, fontSize: 12, color: color.ink }}>{formatUsd(run.costMicros)}</span>
                  </Row>
                </button>
                {open === key && <RunDetail run={run} />}
              </div>
            );
          })
        )}
      </Table>
      {runs !== null && runs.length === 0 && !error && (
        <div style={{ marginTop: 20 }}>
          {narrowed ? (
            <Empty title="No runs match these filters">
              Nothing in the runs fits every filter at once.
              <span style={{ display: 'block', marginTop: 12 }}>
                <HeaderButton onClick={clear}>Clear filters</HeaderButton>
              </span>
            </Empty>
          ) : (
            <Empty title="No runs yet">
              A run appears here when a member’s agent starts a task in one of {organization.name}’s workspaces.
            </Empty>
          )}
        </div>
      )}
      {answer?.cursor && (
        <div style={{ marginTop: 16 }}>
          <HeaderButton
            disabled={older}
            onClick={() => {
              setOlder(true);
              void load(answer.cursor).finally(() => setOlder(false));
            }}
          >
            {older ? 'Loading' : 'Load older runs'}
          </HeaderButton>
        </div>
      )}
      {error && runs !== null && runs.length === 0 ? (
        <PageError onRetry={() => load(null)} />
      ) : (
        error && <p style={{ fontSize: 13.5, color: color.badText, margin: '16px 0 0' }}>{error}</p>
      )}
    </>
  );
}

/** What a row says when it is opened: every count, the hosts, the pull requests. */
function RunDetail({ run }: { run: RunRow }): React.JSX.Element {
  const line = (label: string, value: React.ReactNode): React.JSX.Element => (
    <div style={{ display: 'grid', gridTemplateColumns: '150px minmax(0,1fr)', gap: 12, fontSize: 13 }}>
      <span style={{ color: color.inkFaint }}>{label}</span>
      <span style={{ color: color.inkBody, minWidth: 0, wordBreak: 'break-word' }}>{value}</span>
    </div>
  );
  const hosts = (list: string[]): React.ReactNode =>
    list.length ? <span style={{ fontFamily: font.mono, fontSize: 12 }}>{list.join(', ')}</span> : <span style={{ color: color.inkFaint }}>none</span>;
  return (
    <div style={{ display: 'grid', gap: 8, padding: '14px 22px 18px', background: color.surfaceAlt, borderBottom: `1px solid ${color.line}` }}>
      {line('Ran', `${stamp(run.startedAt)} → ${run.endedAt ? stamp(run.endedAt) : 'still running'}`)}
      {line('Model', `${run.agent} · ${run.model}${run.effort ? ` · ${run.effort}` : ''}`)}
      {line('Work', `${run.turns} turns, ${run.toolCalls} tool calls, ${run.merges} merge${run.merges === 1 ? '' : 's'} — ${run.filesChanged} files, +${run.linesAdded} −${run.linesRemoved}`)}
      {line('Tokens', `${formatTokens(totalTokens(run.tokens))} (${formatTokens(run.tokens.input)} in, ${formatTokens(run.tokens.output)} out, ${formatTokens(run.tokens.cacheRead)} cache read) · ${formatUsd(run.costMicros)} at list prices`)}
      {line('Refused hosts', hosts(run.refusedHosts))}
      {line('Allowed when asked', hosts(run.allowedHosts))}
      {line(
        'Pull requests',
        run.pullRequests.length ? (
          run.pullRequests.map((url) => (
            <a key={url} href={url} target="_blank" rel="noreferrer noopener" style={{ display: 'block', color: color.accentText, fontFamily: font.mono, fontSize: 12 }}>
              {url}
            </a>
          ))
        ) : (
          <span style={{ color: color.inkFaint }}>none</span>
        )
      )}
      {run.taskId && line('Task', <span style={{ fontFamily: font.mono, fontSize: 12 }}>{run.taskId}</span>)}
    </div>
  );
}
